import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import {
  AppointmentDto,
  TimeSlotDto,
  LeaveOfAbsenceDto,
  EventTimeSlotDto,
  UserDto,
  WorkingHoursDto,
  WorkHourExceptionDtoTypeEnum,
  WorkHourExceptionDtoInitiatorEnum,
} from "../../../api-client";
import { useLocationId } from "../../../stores/User/User.hooks";
import {
  useAppointmentsForDate,
  useCachedAppointmentLookup,
  useDerivedWorkingHours,
  useDeltaSummary,
} from "../../../stores/Calendar/calendar.queries";
import { calendarKeys, invalidateCalendarAppointmentCaches } from "../../../stores/Calendar/calendar.keys";
import useStore from "../../../helpers/useStore";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
// import { Event, TimeSlotExt } from "../Calendar.type";
import { ClientTimeSlot, EventType, ClientWorkingHours } from "./adapters/types";
import { CalendarEvent, isCancelledAppointment } from "./adapters";
import { toCalendarResources } from "./CalendarAdapters";
import notificationStore from "../../../stores/Notification/notificationStore";
import { useSocketHandler } from "./SocketHandler";
import {
  useSaveAppointment,
  useUpdateTimeSlot,
  useDeleteWorkHourException,
  useDeleteLunchBreakForDay,
  useCancelDay,
  useCreateWorkingHoursForDay,
  useDeleteTimeSlot,
  useDeleteContinuousTimeSlot,
} from "../../../stores/Calendar/calendar.mutations";
import { useGuardedWorkHourExceptionMutations } from "../lunchBreak/useGuardedWorkHourExceptionMutations";
import {
  eventChangeToAppointmentUpdateData,
  eventChangeToTimeSlotDto,
  eventToClientLunchBreak,
  eventToClientWorkingHours,
} from "./adapters/display-to-client/reverse.adapter";
import { EventChangeArg } from "@fullcalendar/core";
import { useEventShelfController } from "../EventShelf/EventShelfController";
import { CalendarResource } from "./adapters/types";
import { useSelectedUsers } from "./useSelectedUsers";
import { timeSheetToCalendarEvents } from "./adapters/client-to-display/timeSheet.adapter";

// Stable empty collection singletons to avoid new array identities each render
const EMPTY_LOCKED_APPOINTMENTS: string[] = [];

// Utility: returns previous array reference if shallow-equal (by ===) to avoid identity churn
function useShallowStableArray<T>(value: T[] | undefined): T[] {
  const ref = useRef<T[]>(value || []);
  const incoming = value || [];
  if (ref.current.length === incoming.length) {
    let same = true;
    for (let i = 0; i < incoming.length; i++) {
      if (ref.current[i] !== incoming[i]) {
        same = false;
        break;
      }
    }
    if (same) {
      // console.debug(
        // "[useShallowStableArray] No change in array, reusing previous reference",
        // value
      // );
      return ref.current;
    }
  }
  ref.current = incoming;
  // console.debug("[useShallowStableArray] Array changed, updating reference", value);
  return ref.current;
}

export interface TimeSheetData {
  // Core calendar data
  appointments: AppointmentDto[];
  leaveOfAbsences: LeaveOfAbsenceDto[];
  eventTimeslots: EventTimeSlotDto[];
  //   lunchBreaks: WorkingHoursDto[];
  travelTimes: TimeSlotDto[];
  // Flattened calendar events (adapter output) - transitional during refactor
  calendarEvents: CalendarEvent[];

  // Users and resources
  users: UserDto[];
  resources: CalendarResource[];

  // Patient and drawer data
  selectedShelfAppointment?: AppointmentDto;
  unscheduleds: AppointmentDto[];
  shelfAppointments: AppointmentDto[];

  // UI state
  isLoading: boolean;
  selectedDate: Date;
  selectedDateStr: string;
  isContinuousView: boolean;
  isCancelledView: boolean;
  patientDrawerOpen: boolean;
  selectedEventRxId?: string;
  showOpenDrawerButton: boolean;
  lockedAppointments: string[];
  headerWidth: string;
  groupCheck: boolean;
  // Warning dialog state (gap between visit and appointment)
  openWarningDialog: boolean;
  warningMessage: string;
  // Focus position for keyboard navigation persistence
  focusPosition: { resourceId: string; time: string } | null;
  // Cancel day dialog state
  cancelDayDialog: {
    open: boolean;
    userId: string | null;
    userName: string | null;
    date: string | null;
    loading: boolean;
  };
}

export interface TimeSheetHandlers {
  // Date and view handlers
  onDatesSet: (date: Date) => void;

  // Event handlers - using new CalendarEvent type
  onCreateEvent: (event: CalendarEvent) => Promise<void>; // Keep as Event for now (creation uses old interface)
  onChangeEvent: (eventChangeInfo: EventChangeArg) => Promise<void>;
  onRemoveEvent: (id: string, event?: CalendarEvent) => Promise<void>;
  onRemoveWorkingHours: (exceptionId: string) => Promise<void>;
  onRemoveContinuousEvent: (id: string) => Promise<void>;
  onMoveEventDate: (event: CalendarEvent) => Promise<void>;
  onAddShelfEvent: (event: CalendarEvent) => Promise<void>; // Keep as Event for now
  onEventContentClick: (event: ClientTimeSlot) => Promise<void>;
  onSelectedShelfEventReceive: (appointment: AppointmentDto) => Promise<void>;

  // Patient handlers
  onPatientTabChange: (newPatientId: string) => Promise<void>;
  onCloseNotesDialog: () => void;
  onSelectShelfAppointment: (appId: string) => void;
  onDataRefreshFlag: () => Promise<void>;

  // Utility handlers
  onOutdated: () => void;
  renderCalendar: () => Promise<void>;
  onCloseWarningDialog: () => void;
  
  // Cancel day handlers
  onCancelDayClick: (userId: string, date: string, userName: string) => void;
  onCancelDayConfirm: () => Promise<void>;
  onCancelDayCancel: () => void;
}

export const useTimeSheetController = (): {
  data: TimeSheetData;
  handlers: TimeSheetHandlers;
} | null => {
  // S T O R E S   A N D   U I   S T A T E  ******************************************************************
  // URL and location context
  const locationId = useLocationId();
  const queryClient = useQueryClient();

  // Store contexts
  const CalendarStore = useStore(CalendarContext);

  // Calendar store state
  const {
    selectedDateStr,
    isCancelledView,
    setCancelledView,
    selectedPatientId,
    setTargetPatientId,
    patientDrawerOpen,
    continuousView,
    columnUserIds,
    createTimeslot,
    createEventTimeSlot,
    createContinuousEventTimeSlot,
    createLeaveOfAbsence,
    groupCheck,
    pinBoardItems,
    isRoleMode,
    roleForUserList,
    focusPosition,
  } = CalendarStore;

  // Local UI state for warning dialog about mergeAppointmentGaps
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  // Appointment store
  // removed: const { updateContinuousAppointment } = AppointmentStore;

  // Initialize WebSocket event handlers for real-time updates
  useSocketHandler();

  const { handleAddToPinBoard, handleRemoveFromPinBoard } = useEventShelfController();

  const { lookupByAppointmentId } = useCachedAppointmentLookup();

  // Derived state
  // Memoize date object so identity only changes when the string changes
  const selectedDate = useMemo(() => new Date(selectedDateStr), [selectedDateStr]);
  const weeklyWorkDay = true;

  // D A T A  ( Q U E R I E S )  ******************************************************************
  // React Query hooks

  // determine the userIds given the location, date, and role mode
  const { userIds, fetchedUsers, derivedWorkingHours } = useSelectedUsers(
    locationId,
    CalendarStore
  );

  const effectiveUserIds = useMemo(() => {
    const hasUserFilter = userIds && userIds.length > 0;
    return hasUserFilter 
      ? userIds 
      : fetchedUsers.map(u => u.id).filter(Boolean) as string[];
  }, [userIds, fetchedUsers]);

  // Fetch delta summary for timesheet mode (over/under hours) — current day
  const { data: deltaSummaryData } = useDeltaSummary(
    effectiveUserIds,
    moment(selectedDate).format("YYYY-MM-DD"),
    moment(selectedDate).format("YYYY-MM-DD"),
    effectiveUserIds.length > 0 // only fetch when we have users
  );

  // Fetch year-to-date delta summary (Jan 1 – Dec 31 of the selected date's year)
  const selectedYear = moment(selectedDate).year();
  const { data: yearDeltaSummaryData } = useDeltaSummary(
    effectiveUserIds,
    `${selectedYear}-01-01`,
    `${selectedYear}-12-31`,
    effectiveUserIds.length > 0
  );

  // fetch appointments from cache
  const {
    data: cachedAppointments,
    isLoading: appointmentsLoading,
    error: appointmentsError,
  } = useAppointmentsForDate(
    selectedDate,
    locationId!,
    weeklyWorkDay,
    { userIds },
    !continuousView,
    userIds.length > 0
  );

  // fetch continuous appointments for selected user (when single user is selected)
  const selectedUserId = columnUserIds && columnUserIds.length === 1 ? columnUserIds[0] : undefined;

  // M U T A T I O N S        ***************************************************************************
  // Event creation mutation

  //TODO instead of the following mutations, use ones that are relevant to manipulating working hours

  //   const { mutateAsync: saveAppointment } = useSaveAppointment();
  //   const { mutate: updateContinuousTimeSlot } = useUpdateContinuousTimeSlot();
  //   const { mutate: updateTimeSlot } = useUpdateTimeSlot();
  //   const { mutate: patchContinuousTimeSlotTimesAndUser } = usePatchContinuousTimeSlotTimesAndUser();
  //   const { mutate: patchTimeSlotTimesAndUser } = usePatchTimeSlotTimesAndUser();
  //   const { mutateAsync: updateContinuousAppointmentMutation } = useUpdateContinuousAppointment();
  const { createWorkHourException, updateWorkHourException } = useGuardedWorkHourExceptionMutations();
  const { mutateAsync: deleteWorkHourException } = useDeleteWorkHourException();
  const { mutateAsync: deleteLunchBreakForDay } = useDeleteLunchBreakForDay();
  const { mutateAsync: cancelDayMutation } = useCancelDay();
  const { mutateAsync: deleteTimeslot } = useDeleteTimeSlot();
  const { mutateAsync: deleteContinuousTimeslot } = useDeleteContinuousTimeSlot();
  const createWorkingHoursForDay = useCreateWorkingHoursForDay();

  // Context menu and cancel day dialog state
  const [cancelDayDialog, setCancelDayDialog] = useState<{
    open: boolean;
    userId: string | null;
    userName: string | null;
    date: string | null;
    loading: boolean;
  }>({
    open: false,
    userId: null,
    userName: null,
    date: null,
    loading: false,
  });

  // R E F S
  // Refs to hold latest dynamic values for stable callbacks
  const fetchedUsersRef = useRef<UserDto[]>([]);
  const continuousViewRef = useRef<boolean>(continuousView);
  const selectedDateRef = useRef<Date>(selectedDate);
  const locationIdRef = useRef(locationId);

  fetchedUsersRef.current = fetchedUsers;
  continuousViewRef.current = continuousView;
  selectedDateRef.current = selectedDate;
  locationIdRef.current = locationId;

  // Refs for potentially non-stable store/service functions so callbacks can be [] deps
  const deleteTimeslotRef = useRef(deleteTimeslot);
  const deleteContinuousTimeslotRef = useRef(deleteContinuousTimeslot);
  const setTargetPatientIdRef = useRef(setTargetPatientId);
  // Keep latest function references
  deleteTimeslotRef.current = deleteTimeslot;
  deleteContinuousTimeslotRef.current = deleteContinuousTimeslot;
  setTargetPatientIdRef.current = setTargetPatientId;

  // E F F E C T S   A N D   R E N D E R I N G   H A N D L E R S  *********************************************************
  
  /**
   * Validates if resizing working hours would push appointments outside the new working hours boundary
   */
  const validateWorkingHoursResize = (
    newStart: Date, 
    newEnd: Date, 
    userId: string, 
    workingHoursDate: string
  ): { isValid: boolean; conflictingAppointments: AppointmentDto[] } => {
    if (!cachedAppointments?.appointments) {
      return { isValid: true, conflictingAppointments: [] };
    }

    // Filter appointments for the specific user and date
    const userAppointments = cachedAppointments.appointments.filter((apt: AppointmentDto) => 
      apt.timeSlots?.some((ts: TimeSlotDto) => ts.user?.id === userId) &&
      moment(apt.start).format("YYYY-MM-DD") === workingHoursDate
    );

    const conflictingAppointments = userAppointments.filter((apt: AppointmentDto) => {
      const aptStart = new Date(apt.start!);
      const aptEnd = new Date(apt.end!);
      
      // Check if appointment starts before working hours start or ends after working hours end
      return aptStart < newStart || aptEnd > newEnd;
    });

    return {
      isValid: conflictingAppointments.length === 0,
      conflictingAppointments
    };
  };

  /**
   * Helper function to build complete slots array preserving existing exception slots
   */
  const buildCompleteExceptionSlots = (
    existingWorkingHours: WorkingHoursDto[],
    updatedSlot: { slotId?: string; start: string; end: string; isBreak: boolean },
    userId: string,
    date: string
  ) => {
    // Find all existing exception slots for this user+date
    const existingExceptionSlots = existingWorkingHours
      .filter(wh => wh.userId === userId && wh.date === date && wh.source === 'exception')
      .map(wh => ({
        slotId: wh.slotId || undefined,
        start: new Date(wh.start).toISOString(),
        end: new Date(wh.end).toISOString(),
        isBreak: wh.isBreak
      }));

    // Add/update the slot being modified
    const normalizedUpdatedSlot = {
      slotId: updatedSlot.slotId || undefined,
      start: updatedSlot.start,
      end: updatedSlot.end,
      isBreak: updatedSlot.isBreak
    };
    
    if (updatedSlot.slotId) {
      // Update existing slot
      const existingSlotIndex = existingExceptionSlots.findIndex(slot => slot.slotId === updatedSlot.slotId);
      if (existingSlotIndex >= 0) {
        existingExceptionSlots[existingSlotIndex] = normalizedUpdatedSlot;
      } else {
        existingExceptionSlots.push(normalizedUpdatedSlot);
      }
    } else {
      // Add new slot
      existingExceptionSlots.push(normalizedUpdatedSlot);
    }

    // Group slots by type (working hours vs lunch break)
    const workingHoursSlots = existingExceptionSlots.filter(slot => !slot.isBreak);
    const lunchBreakSlots = existingExceptionSlots.filter(slot => slot.isBreak);

    // Build complete slots array - include ALL working hours slots (not just first one)
    const completeSlots = [];

    // Add ALL working hours slots (support multiple work blocks in a day)
    completeSlots.push(...workingHoursSlots);

    // Add ALL lunch break slots — every break has its own stable identity
    // (MD-238 P3): editing one break must never collapse or drop the others.
    completeSlots.push(...lunchBreakSlots);

    return completeSlots;
  };

  /**
   * Handles updates to existing calendar events using the new adapter system.
   * Manages appointment locking during updates to prevent conflicts.
   * Processes both single events and related events for complex appointments.
   * Coordinates database updates for time, date, and user assignment changes.
   */
  const onChangeEvent = async (eventChangeInfo: EventChangeArg) => {
    const event = eventChangeInfo.event as any as CalendarEvent;

    const eventType: EventType = event.extendedProps.eventType;
    const timeSlotType: string = event.extendedProps.timeSlotType;

    switch (eventType) {
      case "working-hours":
        // implement working-hours handling
        if (timeSlotType === "LunchBreak") {
          // if it is exception-derived, then we want to update it, otherwise create it.
          const clientLunchBreak = eventToClientLunchBreak(eventChangeInfo.event);
          
            try {
              const userId = clientLunchBreak.userId;
              const date = moment(clientLunchBreak.start).format("YYYY-MM-DD");
              
              // Build updated lunch break slot
              const updatedLunchBreakSlot = {
                slotId: clientLunchBreak.slotId,
                start: clientLunchBreak.start.toISOString(),
                end: clientLunchBreak.end.toISOString(),
                isBreak: true
              };

              // Check if ANY exception exists for this user+date (either working hours OR lunch break)
              const hasExceptionForDay = derivedWorkingHours?.some(wh => 
                wh.userId === userId && 
                wh.date === date && 
                wh.source === 'exception'
              );

              const existingExceptionId = derivedWorkingHours?.find(wh => 
                wh.userId === userId && 
                wh.date === date && 
                wh.source === 'exception'
              )?.exceptionId;

              if (hasExceptionForDay && existingExceptionId) {
                // Snapshot model: send the COMPLETE day's schedule (work + break).
                // The API stores `slots` verbatim and does not layer planned slots
                // on top, so dropping any slot here would erase that info. The BE
                // is also responsible for collapsing the exception when the
                // resulting snapshot equals the recurring plan.
                const completeSlots = buildCompleteExceptionSlots(
                  derivedWorkingHours || [],
                  updatedLunchBreakSlot,
                  userId,
                  date
                );

                await updateWorkHourException({
                  exceptionId: existingExceptionId,
                  exception: {
                    userId,
                    date,
                    type: WorkHourExceptionDtoTypeEnum.Override,
                    slots: completeSlots,
                    initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
                  },
                });
              } else {
                // Create new exception - preserve any existing working hours
                const completeSlots = buildCompleteExceptionSlots(
                  derivedWorkingHours || [],
                  updatedLunchBreakSlot,
                  userId,
                  date
                );

                await createWorkHourException({
                  exception: {
                    userId,
                    date,
                    type: WorkHourExceptionDtoTypeEnum.Override,
                    slots: completeSlots,
                    initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
                  },
                });
              }
              
              // Refresh working hours data after successful mutation
              await queryClient.invalidateQueries({ queryKey: calendarKeys.workingHours.all() });
            } catch (error) {
              console.error("❌ LUNCH BREAK DRAG OPERATION FAILED:", error);
              console.error("❌ ERROR DETAILS:", {
                message: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
                name: error instanceof Error ? error.name : 'Unknown'
              });
              // DON'T revert automatically - let user see what happened
              // eventChangeInfo.revert();
              const errorMessage = error instanceof Error ? error.message : 'Unknown error';
              notificationStore.showMessage(`Fehler beim Ändern der Pausenzeit: ${errorMessage}`, "error");
            }
        } else {
          // Handle working hours (not lunch break) resizing
          const clientWorkingHours = eventToClientWorkingHours(eventChangeInfo.event);
          
          // // Validate that no appointments would be outside the new working hours
          // const validation = validateWorkingHoursResize(
          //   clientWorkingHours.start,
          //   clientWorkingHours.end,
          //   clientWorkingHours.userId,
          //   moment(clientWorkingHours.start).format("YYYY-MM-DD")
          // );
          
          // if (!validation.isValid) {
          //   const appointmentTitles = validation.conflictingAppointments
          //     .map((apt: AppointmentDto) => {
          //       const patient = apt.timeSlots?.[0]?.patient;
          //       const patientName = patient ? 
          //         `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Unbekannt' :
          //         'Unbekannt';
          //       return `${moment(apt.start).format("HH:mm")} - ${patientName}`;
          //     })
          //     .join(", ");
          //   notificationStore.showMessage(
          //     `Arbeitszeiten können nicht geändert werden. Folgende Termine würden außerhalb der Arbeitszeiten liegen: ${appointmentTitles}`, 
          //     "error"
          //   );
          //   // Revert the event to its original position
          //   eventChangeInfo.revert();
          //   return;
          // }
          
          try {
            const userId = clientWorkingHours.userId;
            const date = moment(clientWorkingHours.start).format("YYYY-MM-DD");
            
            // Build updated working hours slot
            const updatedWorkingHoursSlot = {
              slotId: clientWorkingHours.slotId,
              start: clientWorkingHours.start.toISOString(),
              end: clientWorkingHours.end.toISOString(),
              isBreak: false
            };

            // Check if ANY exception exists for this user+date (either working hours OR lunch break)
            const hasExceptionForDay = derivedWorkingHours?.some(wh => 
              wh.userId === userId && 
              wh.date === date && 
              wh.source === 'exception'
            );

            const existingExceptionId = derivedWorkingHours?.find(wh => 
              wh.userId === userId && 
              wh.date === date && 
              wh.source === 'exception'
            )?.exceptionId;

            if (hasExceptionForDay && existingExceptionId) {
              // Snapshot model: send the COMPLETE day's schedule (work + break).
              // See the lunch-break branch above for the rationale.
              const completeSlots = buildCompleteExceptionSlots(
                derivedWorkingHours || [],
                updatedWorkingHoursSlot,
                userId,
                date
              );

              await updateWorkHourException({
                exceptionId: existingExceptionId,
                exception: {
                  userId,
                  date,
                  type: WorkHourExceptionDtoTypeEnum.Override,
                  slots: completeSlots,
                  initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
                },
              });
            } else {
              // Create new exception - preserve any existing lunch break
              const completeSlots = buildCompleteExceptionSlots(
                derivedWorkingHours || [],
                updatedWorkingHoursSlot,
                userId,
                date
              );

              await createWorkHourException({
                exception: {
                  userId,
                  date,
                  type: WorkHourExceptionDtoTypeEnum.Override,
                  slots: completeSlots,
                  initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
                },
              });
            }
            
            // Refresh working hours data after successful mutation
            await queryClient.invalidateQueries({ queryKey: calendarKeys.workingHours.all() });
          } catch (error) {
            console.error("Error updating working hours:", error);
          }
        }
        break;
      default:
        console.warn("Unknown event type:", eventType);
        notificationStore.showMessage("Unknown event type", "warning");
      // legacyHandleEventChange(eventChangeInfo);
    } // end of switch on event type
  };


  /**
   * Handles date changes in the calendar view.
   * Updates the selected date state and manages data refresh timing.
   * Currently includes throttling logic to prevent excessive API calls during rapid date changes.
   */
  const handleDateChange = useCallback((date: Date) => {
    // setFetchDelay(500);
    // setSelectedDateStr(moment(date).format(DATE_FORMAT));
    // setTimeout(() => setFetchDelay(0), 500);
  }, []);

  /**
   * Toggles between continuous and regular appointment view modes.
   * Manages fetching of different appointment types and clears shelf state.
   * Handles the transition between different calendar display modes with their specific data requirements.
   */
  const handleContinuousViewChange = useCallback(async (continuous: boolean) => {
    // const patientId = patient?.id?.toString();
    // if (patientId) {
    //   await fetchUnscheduledAppointments(patientId, continuous);
    // }
    // setContinuousView(continuous);
    // setShelfAppointments([]);
    // setShelfContinuousAppointments([]);
  }, []);

  /**
   * Toggles the cancelled appointments view filter.
   * Controls whether cancelled/resolved appointments are displayed in the calendar.
   */
  const handleCancelledViewChange = useCallback(
    (checked: boolean) => setCancelledView(checked),
    [setCancelledView]
  );

  /**
   * Creates new events in the calendar system.
   * Processes different event types (GoneFishing, LeaveOfAbsence, EventTimeSlot, etc.)
   * and delegates to appropriate creation services. Handles loading states and user feedback.
   */
  //   const createEventMutateAsyncRef = useRef(createEventMutation.mutateAsync);
  //   createEventMutateAsyncRef.current = createEventMutation.mutateAsync;
  const onCreateEvent = useCallback(async (event: CalendarEvent) => {
    try {
      // Handle WorkingHours type - create working hours exception
      if (event.type === 'WorkingHours') {
        const userId = event.resourceId;
        const date = moment(event.start).format("YYYY-MM-DD");
        const startTime = moment(event.start).format("HH:mm");
        const endTime = moment(event.end).format("HH:mm");

        if (!userId || !date) {
          throw new Error("Cannot create working hours: missing userId or date");
        }

        await createWorkingHoursForDay.mutateAsync({
          userId,
          date,
          startTime,
          endTime,
        });
        return;
      }

      // Handle LunchBreak type (MD-238 P4) — the user manually drew a
      // voluntary break in the timesheet. Send ONLY the break slot as an
      // Override; the backend composes the full day (planned work + this
      // break), keeps other breaks (stable slotIds), enforces the §4 minimum
      // and validates the break lies within working time.
      if (event.type === 'LunchBreak') {
        const userId = event.resourceId;
        if (!userId || !event.start || !event.end) {
          throw new Error("Cannot create break: missing user or time range");
        }
        const date = moment(event.start).format("YYYY-MM-DD");
        const existingException = derivedWorkingHours?.find(
          (wh) => wh.userId === userId && wh.date === date && wh.source === 'exception'
        );
        const breakSlot = {
          isBreak: true,
          start: moment(event.start).toISOString(),
          end: moment(event.end).toISOString(),
        };
        if (existingException?.exceptionId) {
          await updateWorkHourException({
            exceptionId: existingException.exceptionId,
            exception: {
              userId,
              date,
              type: WorkHourExceptionDtoTypeEnum.Override,
              slots: [breakSlot],
              initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
            },
          });
        } else {
          await createWorkHourException({
            exception: {
              userId,
              date,
              type: WorkHourExceptionDtoTypeEnum.Override,
              slots: [breakSlot],
              initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
            },
          });
        }
        return;
      }

      // Other event types handled here...
      // setShowLoading(true);
      //   await createEventMutateAsyncRef.current({ event });
    } catch (error) {
      console.error("Event creation error", error);
    } finally {
      // setShowLoading(false);
    }
  }, [createWorkingHoursForDay, derivedWorkingHours, createWorkHourException, updateWorkHourException]);

  /**
   * Removes regular timeslot events from the calendar and database.
   * Handles deletion of various timeslot types (GoneFishing, EventTimeSlots, etc.).
   * Manages loading states during deletion operations.
   * For lunch breaks, delegates to lunch break deletion (always creates exception to exclude).
   */
  const onRemoveEvent = useCallback(async (timeslotId: string, event?: CalendarEvent) => {
    try {
      // Check if this is a lunch break
      if (event?.extendedProps?.timeSlotType === 'LunchBreak') {
        const userId = event.resourceId || event.extendedProps?.user?.id;
        const date = moment(event.start).format("YYYY-MM-DD");
        
        if (!userId || !date) {
          throw new Error("Cannot delete lunch break: missing userId or date");
        }

        // Per-break delete (MD-238 P3): pass the break's slotId so ONLY the
        // selected break is removed — other breaks of the day stay untouched.
        const slotId = event.extendedProps?.slotId;
        await deleteLunchBreakForDay({ userId, date, slotId });
        return;
      }
      
      // Not a lunch break, use regular deletion
      const result = await deleteTimeslotRef.current(timeslotId);
    } catch (error) {
      console.error("Error in onRemoveEvent:", error);
    }
  }, [deleteLunchBreakForDay]);

  /**
   * Removes working hour exceptions (including lunch breaks and cancelled days).
   * Deletes the exception record, which restores the original weekly plan.
   * This is used for "Plan zurücksetzen" action.
   */
  const onRemoveWorkingHours = useCallback(async (exceptionId: string) => {
    try {
      await deleteWorkHourException({ exceptionId });
      // Refresh working hours data after successful deletion
      await queryClient.invalidateQueries({ queryKey: calendarKeys.workingHours.all() });
      notificationStore.showMessage("Ausnahme erfolgreich gelöscht", "success");
    } catch (error) {
      console.error("Error in onRemoveWorkingHours:", error);
      notificationStore.showMessage("Fehler beim Löschen der Ausnahme", "error");
    }
  }, [deleteWorkHourException, queryClient]);

  /**
   * Removes continuous timeslot events from the calendar and database.
   * Specialized deletion handler for continuous appointment timeslots.
   */
  const onRemoveContinuousEvent = useCallback(async (timeslotId: string) => {
    await deleteContinuousTimeslotRef.current(timeslotId);
  }, []);

  /**
   * Moves appointments from calendar to the shelf for later scheduling.
   * Retrieves full appointment data and adds to appropriate shelf collection.
   * Provides user feedback and manages shelf state for both regular and continuous appointments.
   */
  const onMoveEventDate = useCallback(async (event: CalendarEvent) => {
    // if (continuousView) {
    //   const continuousAppDto: ContinuousAppointmentDto = await getContinuousAppointment(
    //     event.appointmentId!
    //   );
    //   console.log("Event moved to shelf box:", continuousAppDto);
    //   continuousAppDto.timeSlots?.sort(
    //     (a: TimeSlotDto, b: TimeSlotDto) =>
    //       new Date(a.start).getTime() - new Date(b.start).getTime()
    //   );
    //   notificationStore.showMessage("Dauertermin ins Regal verschoben", "info");
    //   setShelfContinuousAppointments([...shelfContinuousAppointments, continuousAppDto]);
    // } else {

    // use lookup instead:

    const appDto: AppointmentDto = lookupByAppointmentId(event.extendedProps.appointmentId!)!;
    appDto.timeSlots?.sort(
      (a: TimeSlotDto, b: TimeSlotDto) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );
    notificationStore.showMessage("Termin ins Regal verschoben", "info");
    // setShelfAppointments([...shelfAppointments, appDto]);
    handleAddToPinBoard(appDto);
    // }
  }, []);

  /**
   * Adds appointments from shelf to specific calendar time slots.
   * Processes the last item from shelf and schedules it at the specified time/resource.
   * Handles both continuous and regular appointments with appropriate save operations.
   * Manages shelf state cleanup and provides user feedback on success/failure.
   */
  const onAddShelfEvent = useCallback(async (event: CalendarEvent) => {
    // console.log("Event moved from shelf box:", event);
    // const lastShelfEvent = continuousView
    //   ? shelfContinuousAppointments[shelfContinuousAppointments.length - 1]
    //   : shelfAppointments[shelfAppointments.length - 1];
    // const userId = event.resourceId || "";
    // const user = fetchedUsers.find((u: UserDto) => u.id === userId);
    // let current = new Date(event.start);
    // if (lastShelfEvent) {
    //   try {
    //     const result = continuousView
    //       ? await updateContinuousWithResponse(
    //           lastShelfEvent.id!,
    //           lastShelfEvent as ContinuousAppointmentDto
    //         )
    //       : await saveAppointment(lastShelfEvent.id!, lastShelfEvent as AppointmentDto);
    //     console.log(result);
    //     setShelfAppointments(shelfAppointments.filter((app) => app.id !== lastShelfEvent.id));
    //     setShelfContinuousAppointments(
    //       shelfContinuousAppointments.filter((app) => app.id !== lastShelfEvent.id)
    //     );
    //     notificationStore.showMessage("Termin erfolgreich hinzugefügt", "success");
    //   } catch (error) {
    //     console.log(error);
    //     notificationStore.showMessage("Fehler beim Hinzufügen des Events", "error", error);
    //   } finally {
    //     await renderCalendar();
    //   }
    // }
  }, []);

  /**
   * Handles patient tab changes in the patient drawer.
   * Fetches patient-specific data when switching between patient tabs.
   * Updates patient context and related appointment information.
   */
  const onPatientTabChange = useCallback(async (newPatientId: string) => {
    // fetchPatient(newPatientId);
    // // TODO: fetchPatientAppointments(newPatientId); - function needs to be implemented
    // fetchUnscheduledAppointments(newPatientId, continuousView);
  }, []);


  /**
   * Handles selection of specific appointments from the unscheduled list.
   * Updates the selected shelf appointment state for keyboard-based scheduling operations.
   */
  const onSelectShelfAppointment = useCallback((appId: string) => {
    // const selectedAppointment = unscheduleds.find((app) => app.id === appId);
    // if (selectedAppointment) {
    //   setSelectedShelfAppointment(selectedAppointment);
    // }
  }, []);

  /**
   * Refreshes unscheduled appointments data for the current patient.
   * Triggered when appointment data needs to be updated due to external changes.
   * Manages loading states during data refresh operations.
   */
  const onDataRefreshFlag = useCallback(async () => {
    // try {
    //   if (!patient?.id) {
    //     console.error("Patient not found");
    //     return;
    //   }
    //   // setShowLoading(true);
    //   await fetchUnscheduledAppointments(patient.id.toString(), continuousView);
    // } catch (error) {
    //   console.error("Error fetching unscheduled appointments:", error);
    // } finally {
    //   // setShowLoading(false);
    // }
  }, []);

  /**
   * Forces a complete calendar data refresh by invalidating React Query caches.
   * Triggers re-fetch of all calendar-related data.
   * Used when calendar data needs to be synchronized with backend changes.
   */
  const invalidateCalendarQueries = useCallback(async () => {
    await Promise.all([
      // Users
      queryClient.invalidateQueries({ queryKey: calendarKeys.usersByLocation.all() }),
      queryClient.invalidateQueries({ queryKey: calendarKeys.usersByRoleAndDate.all() }),
      // Appointments (main day cache + single-therapist date-range cache)
      invalidateCalendarAppointmentCaches(queryClient),
      queryClient.invalidateQueries({ queryKey: calendarKeys.continuousAppointmentsByDay.all() }),
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousAppointmentsForPatient.all(),
      }),
      queryClient.invalidateQueries({ queryKey: calendarKeys.continuousAppointmentsForUser.all() }),
      // Unscheduled
      queryClient.invalidateQueries({
        queryKey: calendarKeys.unscheduledPatientAppointments.all(),
      }),
      queryClient.invalidateQueries({
        queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
      }),
      // Patient availability
      queryClient.invalidateQueries({
        queryKey: calendarKeys.patientUnavailabilityForCalendar.all(),
      }),
      queryClient.invalidateQueries({
        queryKey: calendarKeys.patientUnavailabilityTypeByDateForCalendar.all(),
      }),
      // Working hours & sublocalities
      queryClient.invalidateQueries({ queryKey: calendarKeys.workingHours.all() }),
      queryClient.invalidateQueries({ queryKey: calendarKeys.continuousWorkPlansForUsers.all() }),
      queryClient.invalidateQueries({ queryKey: calendarKeys.sublocalitiesForAppointments.all() }),
    ]);
  }, [queryClient]);

  /**
   * Generic handler for triggering calendar data refresh.
   * Wrapper around renderCalendar for external components to request data updates.
   * Used when other components need to signal that calendar data may be outdated.
   */
  const onOutdated = useCallback(() => {
    console.info("[Calendar Controller Handler] onOutdated called - refreshing data");
    void invalidateCalendarQueries();
  }, [invalidateCalendarQueries]);

  // Prepare filtered appointments based on cancelled view
  const filteredAppointments = useMemo(() => {
    if (!cachedAppointments?.appointments) return [];
    // Build a fast lookup set of pinned appointment ids (only regular appointments with timeSlots)
    const pinnedIds = new Set(
      (pinBoardItems || [])
        .filter((item: any) => item && (item as any).timeSlots && !(item as any).isSlotPin) // distinguish from continuous time slots objects
        .map((item: any) => item.id)
    );
    // Slot-level pins ("Umziehen → Nur diese"): hide ONLY that slot, keep siblings visible.
    const pinnedSlotIds = new Set(
      (pinBoardItems || [])
        .filter((item: any) => item && (item as any).isSlotPin)
        .map((item: any) => item.id)
    );
    // First apply cancelled view logic, then remove pinned items.
    // See isCancelledAppointment for why we don't filter on `end === null`.
    const base = isCancelledView
      ? cachedAppointments.appointments
      : cachedAppointments.appointments.filter(
          (appointment: any) => !isCancelledAppointment(appointment),
        );
    return base
      .filter((appointment: any) => !pinnedIds.has(appointment.id))
      .map((appointment: any) => {
        if (
          pinnedSlotIds.size === 0 ||
          !appointment.timeSlots?.some((ts: any) => pinnedSlotIds.has(ts.id))
        ) {
          return appointment;
        }
        const remainingSlots = appointment.timeSlots.filter(
          (ts: any) => !pinnedSlotIds.has(ts.id)
        );
        return remainingSlots.length > 0 ? { ...appointment, timeSlots: remainingSlots } : null;
      })
      .filter(Boolean);
  }, [cachedAppointments, isCancelledView, pinBoardItems]);

  // P R O C E S S   D A T A   A N D   M E M O I Z A T I O N  *********************************************************
  // data converters and processors to get the final data types ready for the calendar

  // deconstruct appointments cache
  const { leaveOfAbsences, eventTimeSlots, travelTimes } = cachedAppointments || {
    appointments: [],
    goneFishings: [],
    leaveOfAbsences: [],
    eventTimeSlots: [],
    lunchBreaks: [],
    travelTimes: [],
    simpleWorkingHours: [],
  };

  // deconstruct continuous appointments cache

  const {
    appointments: rawContinuousAppointments = [],
    eventTimeSlots: rawContinuousEventTimeslots = [],
  } = { appointments: [], eventTimeSlots: [] };

  // Use user-specific continuous appointments if a single user is selected, otherwise use day-based
  // When a specific user is selected, we want to show their continuous appointments regardless of continuousView mode
  const finalContinuousAppointments = rawContinuousAppointments;

  const finalContinuousEventTimeslots = rawContinuousEventTimeslots;

  // Stabilize continuous arrays (shallow)
  const continuousAppointments = useShallowStableArray(finalContinuousAppointments);
  const continuousEventTimeslots = useShallowStableArray(finalContinuousEventTimeslots);

  const drawerWidth = 320;
  const headerWidth = patientDrawerOpen ? `calc(100% - ${drawerWidth}px)` : "100%";
  // Return data and handlers
  // Stabilize frequently changing query array refs (shallow structural sharing)

  // Generate resources from users using the controller-based approach
  const resources = useMemo(() => {
    if (!fetchedUsers.length) {
      // console.debug("[CalendarController] resources: No fetchedUsers, returning []");
      return [];
    }

    // Filter users based on columnUserIds if provided (for column selection).
    // Empty + role mode means the role-by-date query resolved to nobody for this
    // date, not "show everyone" — see the same guard in CalendarController.
    const filteredUsers =
      columnUserIds && columnUserIds.length > 0
        ? fetchedUsers.filter((user) => columnUserIds.includes(user.id!))
        : isRoleMode
          ? []
          : fetchedUsers;

    // console.debug("[CalendarController] resources calculation:", {
      // fetchedUsersCount: fetchedUsers.length,
      // columnUserIdsCount: columnUserIds?.length || 0,
      // filteredUsersCount: filteredUsers.length,
      // columnUserIds: columnUserIds?.slice(0, 5), // show first 5
    // });

    // Convert delta summary to Map for efficient lookup
    const deltaMinutesMap = deltaSummaryData
      ? new Map(deltaSummaryData.map(d => [d.userId, d.totalDeltaMinutes]))
      : undefined;

    const yearDeltaMinutesMap = yearDeltaSummaryData
      ? new Map(yearDeltaSummaryData.map(d => [d.userId, d.totalDeltaMinutes]))
      : undefined;

    // DEBUG: Log resource creation with delta
    // console.log("[TimeSheetController] Resources with delta:", {
      // filteredUsersCount: filteredUsers.length,
      // hasDeltaMap: !!deltaMinutesMap,
      // deltaMapSize: deltaMinutesMap?.size,
      // deltaMapEntries: deltaMinutesMap ? Array.from(deltaMinutesMap.entries()) : [],
    // });

    return toCalendarResources(filteredUsers, {
      selectedDate,
      isRouteplanView: false, // TODO: get from store if needed
      routeplanUser: undefined, // TODO: get from store if needed
      selectedUser: undefined, // TODO: get from store if needed
      derivedWorkingHours,
      deltaMinutesMap,
      yearDeltaMinutesMap,
    });
  }, [fetchedUsers, columnUserIds, isRoleMode, selectedDate, derivedWorkingHours, deltaSummaryData, yearDeltaSummaryData]);

  // Create a Set of resource IDs for fast lookup
  const resourceIds = useMemo(() => {
    return new Set(resources.map((r) => r.id));
  }, [resources]);

  // convert working hours to lunch breaks, filtered to only include users with visible resources

  const data: TimeSheetData = useMemo(() => {
    const calendarEvents = (() => {
      if (!derivedWorkingHours || derivedWorkingHours.length === 0) {
        return [];
      }
      return timeSheetToCalendarEvents({
        workingHours: derivedWorkingHours,
        appointments: filteredAppointments || [],
        eventTimeSlots: eventTimeSlots|| [],
        // As the API returns them — the adapter needs id and loAType (MD-507).
        leaveOfAbsences: leaveOfAbsences || [],
      });
    })();

    // Debug: Log calendarEvents with their IDs
    // console.debug("[CalendarController] data useMemo - calendarEvents:", {
      // count: calendarEvents.length,
      // resourceIdsCount: resourceIds.size,
      // lunchBreakIds: calendarEvents
        // .filter((e) => e.type === "LunchBreak")
        // .map((e) => ({ id: e.id, resourceId: e.resourceId })),
      // resourceIdsList: Array.from(resourceIds),
    // });

    return {
      appointments: filteredAppointments,
      leaveOfAbsences,
      eventTimeslots: eventTimeSlots,
      // continuousEventTimeslots,
      // continuousAppointments,

      travelTimes,
      calendarEvents,
      selectedShelfAppointment: undefined,
      unscheduleds: [],
      shelfAppointments: [],
      isLoading: false,
      selectedDate,
      selectedDateStr,
      isContinuousView: continuousView,
      isCancelledView,
      patientDrawerOpen,
      selectedEventRxId: undefined,
      showOpenDrawerButton: false,
      lockedAppointments: EMPTY_LOCKED_APPOINTMENTS,
      headerWidth,
      users: fetchedUsers,
      resources,
      groupCheck,
      openWarningDialog,
      warningMessage,
      focusPosition,
      cancelDayDialog,
    };
  }, [
    filteredAppointments,
    leaveOfAbsences,
    eventTimeSlots,
    continuousEventTimeslots,
    continuousAppointments,
    // lunchBreaks,
    travelTimes,
    [],
    [],
    selectedDate,
    selectedDateStr,
    continuousView,
    isCancelledView,
    patientDrawerOpen,
    headerWidth,
    fetchedUsers,
    resources,
    groupCheck,
    openWarningDialog,
    warningMessage,
    selectedUserId,
    focusPosition,
    cancelDayDialog,
  ]);

  const onEventContentClick = useCallback(async (event: ClientTimeSlot) => {}, []);
  const onCloseNotesDialog = useCallback(() => {}, []);
  const renderCalendar = useCallback(async () => {}, []);

  const handleCancelDayClick = useCallback((userId: string, date: string, userName: string) => {
    setCancelDayDialog({ open: true, userId, userName, date, loading: false });
  }, []);

  const handleCancelDayConfirm = useCallback(async () => {
    if (!cancelDayDialog.userId || !cancelDayDialog.date) return;
    setCancelDayDialog(prev => ({ ...prev, loading: true }));
    try {
      await cancelDayMutation({ userId: cancelDayDialog.userId, date: cancelDayDialog.date });
    } finally {
      setCancelDayDialog({ open: false, userId: null, userName: null, date: null, loading: false });
    }
  }, [cancelDayDialog, cancelDayMutation]);

  const handleCancelDayCancel = useCallback(() => {
    setCancelDayDialog({ open: false, userId: null, userName: null, date: null, loading: false });
  }, []);

  const handlers: TimeSheetHandlers = useMemo(
    () => ({
      onDatesSet: handleDateChange,
      onCreateEvent,
      onChangeEvent,
      onRemoveEvent,
      onRemoveWorkingHours,
      onRemoveContinuousEvent,
      onMoveEventDate,
      onAddShelfEvent,
      onEventContentClick,
      onSelectedShelfEventReceive: async () => {},
      onPatientTabChange,
      onCloseNotesDialog: () => {},
      onSelectShelfAppointment,
      onDataRefreshFlag,
      onOutdated,
      renderCalendar,
      onCloseWarningDialog: () => setOpenWarningDialog(false),
      onCancelDayClick: handleCancelDayClick,
      onCancelDayConfirm: handleCancelDayConfirm,
      onCancelDayCancel: handleCancelDayCancel,
    }),
    [
      handleDateChange,
      handleContinuousViewChange,
      handleCancelledViewChange,
      onCreateEvent,
      onChangeEvent,
      onRemoveEvent,
      onRemoveContinuousEvent,
      onMoveEventDate,
      onAddShelfEvent,
      onEventContentClick,
      onPatientTabChange,
      onCloseNotesDialog,
      onSelectShelfAppointment,
      onDataRefreshFlag,
      onOutdated,
      renderCalendar,
      setOpenWarningDialog,
      handleCancelDayClick,
      handleCancelDayConfirm,
      handleCancelDayCancel,
    ]
  );

  return { data, handlers };
};
